import { useSyncExternalStore } from 'react'

export type DockedDrawerId = 'chat' | 'notes'

export type DockedDrawerState = {
  open: boolean
  docked: boolean
  width: number
}

const DRAWER_ORDER: DockedDrawerId[] = ['notes', 'chat']

const state: Record<DockedDrawerId, DockedDrawerState> = {
  chat: { open: false, docked: false, width: 0 },
  notes: { open: false, docked: false, width: 0 },
}

const listeners = new Set<() => void>()

function emit(): void {
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Publish open/docked/width so stacked drawers can offset from the right edge. */
export function setDockedDrawerState(id: DockedDrawerId, partial: Partial<DockedDrawerState>): void {
  const prev = state[id]!
  const next = { ...prev, ...partial }
  if (prev.open === next.open && prev.docked === next.docked && prev.width === next.width) {
    return
  }
  state[id] = next
  emit()
}

/** Fixed `right` offset for a drawer panel (sum of docked widths to its right). */
export function getDockedDrawerRightOffset(id: DockedDrawerId): number {
  const index = DRAWER_ORDER.indexOf(id)
  let offset = 0
  for (let i = index + 1; i < DRAWER_ORDER.length; i++) {
    const drawer = state[DRAWER_ORDER[i]!]!
    if (drawer.open && drawer.docked) {
      offset += drawer.width
    }
  }
  return offset
}

/** FAB offset when a drawer to the left is open and this drawer's panel is closed. */
export function getDockedDrawerFabRightOffset(id: DockedDrawerId): number {
  const index = DRAWER_ORDER.indexOf(id)
  const self = state[id]!
  if (self.open) return getDockedDrawerRightOffset(id)
  for (let i = index - 1; i >= 0; i--) {
    const drawer = state[DRAWER_ORDER[i]!]!
    if (drawer.open) {
      return drawer.width
    }
  }
  return getDockedDrawerRightOffset(id)
}

export function useDockedDrawerRightOffset(id: DockedDrawerId): number {
  return useSyncExternalStore(
    subscribe,
    () => getDockedDrawerRightOffset(id),
    () => 0
  )
}

export function useDockedDrawerFabRightOffset(id: DockedDrawerId): number {
  return useSyncExternalStore(
    subscribe,
    () => getDockedDrawerFabRightOffset(id),
    () => 0
  )
}
